/**
 * publishLayouts — PDF-4 PUBLISH: assemble N ordered sheets into ONE multi-page
 * PDF (the core user value: "листы можно печатать по одному или выделить
 * несколько сразу", each sheet with its own paper / plot area / scale).
 *
 * One `PDFDocument`, one page per sheet (via the shared {@link renderSheetPage}),
 * so pages may differ in size. PLOT (a single sheet) stays `exportLayoutPdf`.
 * Empty-region sheets still emit a blank page + a warning (the user selected
 * them — we do NOT skip).
 *
 * Adds two PUBLISH features:
 *
 *   - **Bookmarks** (P3) — a PDF outline (`/Outlines` in the catalog) with one
 *     top-level item per page, named by the sheet (title ?? layout name). pdf-lib
 *     has no high-level outline API, so the outline dictionaries are built raw
 *     (`context.obj` + `context.register` + `catalog.set('/Outlines')`), linking
 *     First/Last/Next/Prev/Parent/Count/Dest (Dest → `[pageRef /Fit]`). Mirrors
 *     the raw-dict pattern PLAN Appendix D describes for OCG / ExtGState.
 *
 *   - **PLOTSTAMP** (P3) — an optional per-sheet stamp (filename · layout name ·
 *     date · plot scale) drawn in the lower-left corner of the printable area
 *     (~2.5 mm tall), gated behind `options.plotStamp` (global) or a per-sheet
 *     `stamp` flag. Rendered through the same text-paint path as page text.
 *
 * # Deferred (PDF-4b — documented, NOT faked)
 *   - **Hyperlinks** (clickable link annotations) — PLAN lists them, but they
 *     are niche. Deferred to PDF-4b; see {@link HyperlinkNote}. Bookmarks +
 *     PLOTSTAMP (the PUBLISH value) ship now.
 *   - **Separate-files mode** — AutoCAD PUBLISH can emit one file per sheet.
 *     PDF-4 ships the multi-page (single file) default; per-file is PDF-4b.
 */

import {
  PDFDocument,
  PDFName,
  PDFNumber,
  PDFString,
  PDFArray,
  type PDFPage,
  type PDFRef,
} from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

import {
  defaultPlotSettings,
  PlotAreaType,
  printableAreaMm,
  type DrawingDocument,
  type LayoutPlotSettings,
  type TextStyle,
} from "@dxf-ai-editor/drawing-model";
import type { PlotStyleTable } from "@dxf-ai-editor/acadctb";

import { PLOT_BLACK, type PdfTransform } from "./pdfTransform";
import { buildPdfTransform } from "./pdfTransform";
import {
  paintTextRuns,
  type FontEmbedder,
  type TextPaintResult,
} from "./textPainter";
import type { TextRunPrimitive } from "./cadPrimitive";
import type { ResolveFontOptions } from "./fontResolve";
import {
  renderSheetPage,
  type SheetRenderStats,
  type ViewportPlotStats,
} from "./sheetRenderer";
import { resolveSpace } from "./exportLayoutPdf";
import { createOcgController } from "./ocg";

/**
 * PDF-4b deferral note: clickable hyperlink annotations (Link annots with a URI
 * / GoTo action) are listed in PLAN but deferred. Bookmarks + PLOTSTAMP ship in
 * PDF-4. Documented, not faked.
 */
export const HyperlinkNote =
  "PDF-4b: hyperlink annotations deferred (bookmarks + PLOTSTAMP shipped in PDF-4)";

/** One sheet to publish (ordered). */
export interface PublishSheet {
  /** Space id (layout) to render. */
  spaceId: string;
  /** Plot settings for this sheet. Defaults to `defaultPlotSettings()`. */
  settings?: LayoutPlotSettings;
  /** Bookmark / PLOTSTAMP title. Defaults to the space name. */
  title?: string;
  /** Per-sheet PLOTSTAMP override (else `options.plotStamp`). */
  stamp?: boolean;
}

/** Options for {@link publishLayouts}. */
export interface PublishLayoutsOptions {
  /** Paper-space tessellation tolerance in mm (default 0.1). */
  toleranceMm?: number;
  /** PDF document title metadata. */
  title?: string;
  /** Font-resolution overrides for the text pass. */
  fontResolve?: ResolveFontOptions;
  /**
   * Draw a PLOTSTAMP on every sheet (overridable per sheet via `sheet.stamp`).
   * Default false. The stamp reads "<file> · <layout> · <date> · <scale>".
   */
  plotStamp?: boolean;
  /** Filename shown in the PLOTSTAMP. Defaults to the document title / "drawing". */
  fileName?: string;
  /**
   * PDF-6.9b — layer NAMES the user un-checked in the "Include Layers" panel
   * of the PDF Options sub-dialog. Forwarded to every sheet's
   * `renderSheetPage(...)` call. Empty / absent → every layer included.
   */
  omitLayers?: ReadonlyArray<string>;
  /**
   * PDF-6.9b — whether to build the multi-sheet bookmarks (`/Outlines`) tree.
   * Default `true` (PUBLISH historically always wrote bookmarks). When
   * `false`, the outline build is skipped entirely (`bookmarkCount === 0` in
   * the result, no `/Outlines` catalog entry).
   */
  includeBookmarks?: boolean;
  /**
   * PDF-6.3b — the loaded CTB / STB plot-style table from the PDF Options
   * sub-dialog. Forwarded into each sheet's {@link CadConvertContext} so the
   * per-entity style resolution applies the override paths uniformly across
   * the whole multi-sheet PUBLISH. Absent → no overrides.
   */
  plotStyleTable?: PlotStyleTable;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting. Forwarded
   * to EVERY sheet of the multi-page PUBLISH (one user choice → uniform blend
   * mode across the whole batch). Default / absent → `"overwrite"`.
   */
  mergeControl?: "overwrite" | "merge";
}

/** Per-page accounting for a published sheet. */
export interface PageStat extends SheetRenderStats {
  /** Sheet space id. */
  spaceId: string;
  /** Bookmark title used for this page. */
  title: string;
  /** Zero-based page index in the output PDF. */
  pageIndex: number;
}

/** Result of a PUBLISH. */
export interface PublishLayoutsResult {
  bytes: Uint8Array;
  /** Per-page stats (one per requested sheet, in order). */
  pages: PageStat[];
  /** Number of bookmark (outline) items written (== pages.length). */
  bookmarkCount: number;
  /** Number of PLOTSTAMPs drawn. */
  stampsDrawn: number;
  /** Number of empty (blank) sheets emitted. */
  emptyCount: number;
}

/**
 * Render an ordered list of sheets into ONE multi-page PDF. Each sheet gets its
 * own page sized to its physical paper, with its own plot area + scale. Adds a
 * bookmark outline (one item per page) and optional PLOTSTAMPs.
 */
export async function publishLayouts(
  doc: DrawingDocument,
  sheets: readonly PublishSheet[],
  options: PublishLayoutsOptions = {},
): Promise<PublishLayoutsResult> {
  const pdf = await PDFDocument.create();
  if (options.title) pdf.setTitle(options.title);

  const textStyleMap = buildTextStyleMap(doc.styleTables?.textStyles ?? []);
  const embedder = pdf as unknown as FontEmbedder;
  const fileName = options.fileName ?? options.title ?? doc.title ?? "drawing";
  const today = new Date().toISOString().slice(0, 10);

  const pages: PageStat[] = [];
  const pageRefs: PDFRef[] = [];
  const titles: string[] = [];
  let stampsDrawn = 0;
  let emptyCount = 0;

  // PDF-6.2b: ONE optional-content controller for the whole multi-page document
  // (OCGs + `/OCProperties` are catalog-level). Each page's Resources maps the
  // OCG property keys IT uses; a layer that appears on several sheets is ONE
  // shared OCG. Finalized ONCE after the last page (below).
  const ocgController = createOcgController(pdf);

  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets[i]!;
    const settings = sheet.settings ?? defaultPlotSettings();
    const space = resolveSpace(doc, sheet.spaceId);
    const title = sheet.title ?? space.name ?? `Sheet ${i + 1}`;

    const beforeCount = pdf.getPageCount();
    const stats: SheetRenderStats = await renderSheetPage(pdf, doc, space, {
      settings,
      toleranceMm: options.toleranceMm,
      fontResolve: options.fontResolve,
      ocgController,
      // PDF-6.9b: per-sheet omit list (shared across all pages of one PUBLISH;
      // omits a layer from EVERY sheet in the run, since the user picked one
      // PDF Options config for the whole batch).
      omitLayers: options.omitLayers,
      // PDF-6.3b: forward the loaded plot-style table — same instance applied
      // to every sheet of the multi-page PUBLISH (the user picked one CTB/STB
      // for the whole batch).
      plotStyleTable: options.plotStyleTable,
      // PDF-6.4: same Lines-merge / Overwrite choice applies uniformly across
      // every sheet in the batch.
      mergeControl: options.mergeControl,
    });
    // The page renderSheetPage just added is the last one.
    const pageIndex = beforeCount;
    const page = pdf.getPage(pageIndex);
    pageRefs.push(page.ref);
    titles.push(title);

    if (stats.empty) emptyCount += 1;

    // PLOTSTAMP (P3): per-sheet override else the global flag. Skipped on empty
    // sheets where the printable collapsed (§7.3) — no place to draw it. The
    // stamp's text-paint result is folded into the page's `text` accounting.
    const wantStamp = sheet.stamp ?? options.plotStamp ?? false;
    if (wantStamp && !isPrintableCollapsed(settings)) {
      const stampResult = await drawPlotStamp(page, embedder, settings, {
        fileName,
        layoutName: title,
        date: today,
        textStyles: textStyleMap,
        fontResolve: options.fontResolve,
      });
      mergeTextResult(stats.text, stampResult);
      stampsDrawn += 1;
    }

    pages.push({ ...stats, spaceId: sheet.spaceId, title, pageIndex });
  }

  // PDF-6.2b: write `/OCProperties` (every OCG created across all pages, all ON,
  // stable layer order) once now that every page has been rendered.
  ocgController.finalize();

  // Bookmarks (P3): build a raw /Outlines tree, one top-level item per page.
  // PDF-6.9b: gate on `options.includeBookmarks` (default `true`). When OFF,
  // skip the outline build entirely — no `/Outlines` catalog entry, no item
  // dicts registered. The result still reports `bookmarkCount = 0` so callers
  // can verify the gate took effect.
  const bookmarksOn = options.includeBookmarks ?? true;
  const bookmarkCount = bookmarksOn ? buildOutline(pdf, pageRefs, titles) : 0;

  const bytes = await pdf.save();
  return { bytes, pages, bookmarkCount, stampsDrawn, emptyCount };
}

/** Whether the printable area collapsed (margins ≥ paper) — no stamp room. */
function isPrintableCollapsed(settings: LayoutPlotSettings): boolean {
  const p = printableAreaMm(settings);
  return !(p.width > 0) || !(p.height > 0);
}

/** Build a `styleName → TextStyle` lookup. */
function buildTextStyleMap(
  styles: readonly TextStyle[],
): ReadonlyMap<string, TextStyle> {
  const map = new Map<string, TextStyle>();
  for (const s of styles) map.set(s.name, s);
  return map;
}

/* ── PLOTSTAMP ──────────────────────────────────────────────────────────── */

/** PLOTSTAMP geometry: ~2.5 mm tall, just inside the printable lower-left. */
const PLOTSTAMP_HEIGHT_MM = 2.5;
const PLOTSTAMP_INSET_MM = 1.5;

interface PlotStampContext {
  fileName: string;
  layoutName: string;
  date: string;
  textStyles: ReadonlyMap<string, TextStyle>;
  fontResolve?: ResolveFontOptions;
}

/**
 * Draw a single-line PLOTSTAMP in the lower-left of the printable area. The
 * stamp text is "<file> · <layout> · <date> · Scale <n:d>". Rendered via the
 * shared {@link paintTextRuns} so it uses the same embedded-font path as page
 * text (searchable). A dedicated identity page transform places the stamp in
 * raw printable mm (no plot scale / region offset — the stamp is sheet
 * furniture, drawn at 1:1 like AutoCAD's plot stamp).
 */
async function drawPlotStamp(
  page: PDFPage,
  embedder: FontEmbedder,
  settings: LayoutPlotSettings,
  stamp: PlotStampContext,
): Promise<TextPaintResult> {
  const scaleText =
    settings.scale.numerator === settings.scale.denominator
      ? "1:1"
      : `${settings.scale.numerator}:${settings.scale.denominator}`;
  const value = `${stamp.fileName} · ${stamp.layoutName} · ${stamp.date} · Scale ${scaleText}`;

  // Identity printable transform: place at raw printable mm (origin at the
  // printable lower-left, no plot-region localize / fit scale). FIX E (Codex
  // #180): the shallow copy MUST force `plotAreaType: Layout` so the stamp is
  // always drawn at TRUE printable mm (1:1). Keeping the sheet's area (e.g.
  // Window) made `buildPdfTransform` resolve the sheet's plot scale
  // (numerator/denominator — 0.5 on a 1:2 sheet), halving the inset. The stamp
  // is printable furniture and must sit at the same printable position
  // regardless of the sheet's plot area / scale. We also reset `scale` to 1:1
  // so any downstream lineweight/scale read on the copy is unambiguous.
  const stampSettings: LayoutPlotSettings = {
    ...settings,
    plotAreaType: PlotAreaType.Layout,
    scale: { numerator: 1, denominator: 1 },
    offset: { x: 0, y: 0 },
    rotation: 0,
  };
  const stampTransform: PdfTransform = buildPdfTransform({
    settings: stampSettings,
  });

  const run: TextRunPrimitive = {
    type: "text-run",
    entityId: "__plotstamp__",
    // PDF-6.2a: the plot stamp is synthetic plot furniture, not a drawing
    // entity, so it files under layer "0" (it has no real OCG layer; in 6.2b a
    // stamp paints outside any optional-content group).
    layer: "0",
    position: { x: PLOTSTAMP_INSET_MM, y: PLOTSTAMP_INSET_MM },
    height: PLOTSTAMP_HEIGHT_MM,
    rotation: 0,
    widthFactor: 1,
    obliqueAngle: 0,
    hJustification: 0,
    vJustification: 0,
    styleName: "Standard",
    value,
    color: PLOT_BLACK,
  };

  return paintTextRuns(page, embedder, [run], stampTransform, stampSettings, {
    textStyles: stamp.textStyles,
    fontResolve: stamp.fontResolve,
    fontkit,
  });
}

/** Fold a {@link TextPaintResult} into a running accumulator (in place). */
function mergeTextResult(acc: TextPaintResult, add: TextPaintResult): void {
  acc.renderedRuns += add.renderedRuns;
  acc.ttfRuns += add.ttfRuns;
  acc.shxRuns += add.shxRuns;
  acc.embeddedFonts += add.embeddedFonts;
  acc.shxPathOps += add.shxPathOps;
  acc.substitutedRuns += add.substitutedRuns;
  acc.fallbackRuns += add.fallbackRuns;
  acc.subsetRetries += add.subsetRetries;
}

/* ── Bookmarks (PDF outline) ─────────────────────────────────────────────── */

/**
 * Build a flat one-level outline: each page gets a top-level outline item whose
 * Dest is `[pageRef /Fit]`. Links First/Last/Next/Prev/Parent/Count manually
 * (pdf-lib has no outline helper). Returns the number of items written.
 *
 * The /Outlines root carries `/Type /Outlines`, `/Count <n>` (open items) and
 * First/Last child refs; each item carries `/Title`, `/Parent`, `/Dest`, and
 * Prev/Next sibling refs. Registered indirect objects, then attached to the
 * document catalog via `/Outlines`.
 */
function buildOutline(
  pdf: PDFDocument,
  pageRefs: readonly PDFRef[],
  titles: readonly string[],
): number {
  if (pageRefs.length === 0) return 0;
  const context = pdf.context;

  // Reserve a ref for the outline root so items can point /Parent at it.
  const rootRef = context.nextRef();

  // Reserve one ref per item up front so we can link siblings.
  const itemRefs: PDFRef[] = pageRefs.map(() => context.nextRef());

  for (let i = 0; i < itemRefs.length; i++) {
    const dest = PDFArray.withContext(context);
    dest.push(pageRefs[i]!);
    dest.push(PDFName.of("Fit"));

    const dict = context.obj({
      Title: PDFString.of(titles[i] ?? `Sheet ${i + 1}`),
      Parent: rootRef,
      Dest: dest,
    });
    if (i > 0) dict.set(PDFName.of("Prev"), itemRefs[i - 1]!);
    if (i < itemRefs.length - 1) dict.set(PDFName.of("Next"), itemRefs[i + 1]!);
    context.assign(itemRefs[i]!, dict);
  }

  const root = context.obj({
    Type: PDFName.of("Outlines"),
    First: itemRefs[0]!,
    Last: itemRefs[itemRefs.length - 1]!,
    Count: PDFNumber.of(itemRefs.length),
  });
  context.assign(rootRef, root);

  pdf.catalog.set(PDFName.of("Outlines"), rootRef);
  return itemRefs.length;
}

export type { ViewportPlotStats };
